// src/pages/vipUrl.ts
import querystring from "querystring";
import { seoUrlPart } from "@ecg-internal/aurora-js-formatters";

// src/utils/hashPageName.ts
import crypto from "crypto";
function hashPageName_default({ name, secret }) {
  const hash = crypto.createHash("md5");
  hash.update(`${name}{${secret}}`);
  return hash.digest("hex");
}

// src/pages/vipUrl.ts
var createUrlParams = (previousPage, extraQueryParams) => {
  const urlParams = {};
  if (previousPage?.name) {
    urlParams.c = hashPageName_default({
      name: previousPage.name,
      secret: previousPage.secret
    });
  }
  if (extraQueryParams) {
    Object.keys(extraQueryParams).forEach((key) => {
      urlParams[key] = extraQueryParams[key];
    });
  }
  if (!Object.keys(urlParams).length) {
    return "";
  }
  return `?${querystring.stringify(urlParams)}`;
};
var vipUrl_default = ({
  previousPage,
  parentCategoryName,
  categoryName,
  itemId,
  title,
  noParams = false,
  extraQueryParams,
  isOldVip = false
}) => {
  const l1Path = seoUrlPart(parentCategoryName);
  const l2Path = seoUrlPart(categoryName);
  const itemIdPath = itemId;
  const titlePath = seoUrlPart(title);
  const urlParams = noParams ? "" : createUrlParams(previousPage, extraQueryParams);
  if (isOldVip) {
    return `/a/${l1Path}/${l2Path}/${itemIdPath}-${titlePath}.html${urlParams}`;
  }
  return `/v/${l1Path}/${l2Path}/${itemIdPath}-${titlePath}${urlParams}`;
};

// src/pages/asqUrl.ts
var asqUrl_default = ({ itemId, previousAction }) => {
  let url = `/asq?itemId=${itemId}`;
  if (previousAction) {
    url = `${url}&previousAction=${previousAction}`;
  }
  return url;
};

// src/pages/asqModalUrl.ts
var createHashParams = (previousAction, messageType) => {
  const hashParams = {
    action: "showAsq",
    ...messageType && { asqMessageType: messageType },
    ...previousAction && { previousAction }
  };
  return `#${new URLSearchParams(hashParams).toString()}`;
};
var asqModalUrl = ({
  previousPage,
  previousAction,
  messageType,
  parentCategoryName,
  categoryName,
  itemId,
  title
}) => {
  const vipUrl = vipUrl_default({
    previousPage,
    parentCategoryName,
    categoryName,
    itemId,
    title
  });
  const hashParams = createHashParams(previousAction, messageType);
  return `${vipUrl}${hashParams}`;
};
var asqModalUrl_default = asqModalUrl;

// src/constants/urlPrefixes.ts
var urlPrefixes_default = {
  baseCategoriesURL: "l",
  attributes: "f",
  textAttribute: "ta",
  query: "q",
  page: "p",
  sorting: "s",
  seller: "u",
  traits: "t",
  nonTrackingSeparator: "|",
  nonTrackingKey: "#",
  nonTrackingDefiner: ":",
  seoFriendlyMergeKey: "+",
  pageOffset: "offset",
  Limit: "limit",
  distance: "distanceMeters",
  postcode: "postcode",
  view: "view",
  searchInTitleAndDescription: "searchInTitleAndDescription",
  asSavedSearch: "asSavedSearch",
  bypassSpellingSuggestion: "bypassSpellingSuggestion",
  offeredSince: "offeredSince"
};

// src/utils/formatters/formatForURL.ts
import { seoUrlPart as seoUrlPart2 } from "@ecg-internal/aurora-js-formatters";

// src/constants/supportedBooleanOperators.ts
var supportedBooleanOperators_default = ["AND", "OR", "NOT"];

// src/constants/accentedCharacterList.ts
var accentedCharacterList_default = [
  "\xE0",
  // à  [LATIN SMALL LETTER A WITH GRAVE
  "\xE1",
  // á  [LATIN SMALL LETTER A WITH ACUTE]
  "\xE2",
  // â  [LATIN SMALL LETTER A WITH CIRCUMFLEX]
  "\xE3",
  // ã  [LATIN SMALL LETTER A WITH TILDE]
  "\xE4",
  // ä  [LATIN SMALL LETTER A WITH DIAERESIS]
  "\xE5",
  // å  [LATIN SMALL LETTER A WITH RING ABOVE]
  "\xC0",
  // À  [LATIN CAPITAL LETTER A WITH GRAVE]
  "\xC1",
  // Á  [LATIN CAPITAL LETTER A WITH ACUTE]
  "\xC2",
  // Â  [LATIN CAPITAL LETTER A WITH CIRCUMFLEX]
  "\xC3",
  // Ã  [LATIN CAPITAL LETTER A WITH TILDE]
  "\xC4",
  // Ä  [LATIN CAPITAL LETTER A WITH DIAERESIS]
  "\xC5",
  // Å  [LATIN CAPITAL LETTER A WITH RING ABOVE]
  "\xC7",
  // Ç  [LATIN CAPITAL LETTER C WITH CEDILLA]
  "\u0106",
  // Ć  [LATIN CAPITAL LETTER C WITH ACUTE]
  "\u0108",
  // Ĉ  [LATIN CAPITAL LETTER C WITH CIRCUMFLEX]
  "\u010C",
  // Č  [LATIN CAPITAL LETTER C WITH CARON]
  "\xE7",
  // ç  [LATIN SMALL LETTER C WITH CEDILLA]
  "\u0107",
  // ć  [LATIN SMALL LETTER C WITH ACUTE]
  "\u0109",
  // ĉ  [LATIN SMALL LETTER C WITH CIRCUMFLEX]
  "\u010D",
  // č  [LATIN SMALL LETTER C WITH CARON]
  "\xC8",
  // È  [LATIN CAPITAL LETTER E WITH GRAVE]
  "\xC9",
  // É  [LATIN CAPITAL LETTER E WITH ACUTE]
  "\xCA",
  // Ê  [LATIN CAPITAL LETTER E WITH CIRCUMFLEX]
  "\xCB",
  // Ë  [LATIN CAPITAL LETTER E WITH DIAERESIS]
  "\xE8",
  // è  [LATIN SMALL LETTER E WITH GRAVE]
  "\xE9",
  // é  [LATIN SMALL LETTER E WITH ACUTE]
  "\xEA",
  // ê  [LATIN SMALL LETTER E WITH CIRCUMFLEX]
  "\xEB",
  // ë  [LATIN SMALL LETTER E WITH DIAERESIS]
  "\xCC",
  // Ì  [LATIN CAPITAL LETTER I WITH GRAVE]
  "\xCD",
  // Í  [LATIN CAPITAL LETTER I WITH ACUTE]
  "\xCE",
  // Î  [LATIN CAPITAL LETTER I WITH CIRCUMFLEX]
  "\xCF",
  // Ï  [LATIN CAPITAL LETTER I WITH DIAERESIS]
  "\xEC",
  // ì  [LATIN SMALL LETTER I WITH GRAVE]
  "\xED",
  // í  [LATIN SMALL LETTER I WITH ACUTE]
  "\xEE",
  // î  [LATIN SMALL LETTER I WITH CIRCUMFLEX]
  "\xEF",
  // ï  [LATIN SMALL LETTER I WITH DIAERESIS]
  "\xD1",
  // Ñ  [LATIN CAPITAL LETTER N WITH TILDE]
  "\xF1",
  // ñ  [LATIN SMALL LETTER N WITH TILDE]
  "\xD2",
  // Ò  [LATIN CAPITAL LETTER O WITH GRAVE]
  "\xD3",
  // Ó  [LATIN CAPITAL LETTER O WITH ACUTE]
  "\xD4",
  // Ô  [LATIN CAPITAL LETTER O WITH CIRCUMFLEX]
  "\xD5",
  // Õ  [LATIN CAPITAL LETTER O WITH TILDE]
  "\xD6",
  // Ö  [LATIN CAPITAL LETTER O WITH DIAERESIS]
  "\xD8",
  // Ø  [LATIN CAPITAL LETTER O WITH STROKE]
  "\xF2",
  // ò  [LATIN SMALL LETTER O WITH GRAVE]
  "\xF3",
  // ó  [LATIN SMALL LETTER O WITH ACUTE]
  "\xF4",
  // ô  [LATIN SMALL LETTER O WITH CIRCUMFLEX]
  "\xF5",
  // õ  [LATIN SMALL LETTER O WITH TILDE]
  "\xF6",
  // ö  [LATIN SMALL LETTER O WITH DIAERESIS]
  "\xF8",
  // ø  [LATIN SMALL LETTER O WITH STROKE]
  "\u015A",
  // Ś  [LATIN CAPITAL LETTER S WITH ACUTE]
  "\u015C",
  // Ŝ  [LATIN CAPITAL LETTER S WITH CIRCUMFLEX]
  "\u015E",
  // Ş  [LATIN CAPITAL LETTER S WITH CEDILLA]
  "\u0160",
  // Š  [LATIN CAPITAL LETTER S WITH CARON]
  "\u015B",
  // ś  [LATIN SMALL LETTER S WITH ACUTE]
  "\u015D",
  // ŝ  [LATIN SMALL LETTER S WITH CIRCUMFLEX]
  "\u015F",
  // ş  [LATIN SMALL LETTER S WITH CEDILLA]
  "\u0161",
  // š  [LATIN SMALL LETTER S WITH CARON]
  "\xD9",
  // Ù  [LATIN CAPITAL LETTER U WITH GRAVE]
  "\xDA",
  // Ú  [LATIN CAPITAL LETTER U WITH ACUTE]
  "\xDB",
  // Û  [LATIN CAPITAL LETTER U WITH CIRCUMFLEX]
  "\xDC",
  // Ü  [LATIN CAPITAL LETTER U WITH DIAERESIS]
  "\xF9",
  // ù  [LATIN SMALL LETTER U WITH GRAVE]
  "\xFA",
  // ú  [LATIN SMALL LETTER U WITH ACUTE]
  "\xFB",
  // û  [LATIN SMALL LETTER U WITH CIRCUMFLEX]
  "\xFC",
  // ü  [LATIN SMALL LETTER U WITH DIAERESIS]
  "\xDD",
  // Ý  [LATIN CAPITAL LETTER Y WITH ACUTE]
  "\u0176",
  // Ŷ  [LATIN CAPITAL LETTER Y WITH CIRCUMFLEX]
  "\u0178",
  // Ÿ  [LATIN CAPITAL LETTER Y WITH DIAERESIS]
  "\xFD",
  // ý  [LATIN SMALL LETTER Y WITH ACUTE]
  "\xFF",
  // ÿ  [LATIN SMALL LETTER Y WITH DIAERESIS]
  "\u0177",
  // ŷ  [LATIN SMALL LETTER Y WITH CIRCUMFLEX]
  "\u017D",
  // Ž  [LATIN CAPITAL LETTER Z WITH CARON]
  "\u017E"
  // ž  [LATIN SMALL LETTER Z WITH CARON]
];

// src/utils/formatters/formatForURL.ts
var isNumber = (value) => (
  /* (IE doesn't support Number.isNan() and Number.isFinite()) */
  !isNaN(parseFloat(value)) && isFinite(value)
);
var formatWord = (input) => {
  if (input === null || typeof input === "undefined") {
    return "";
  }
  if (isNumber(input)) {
    return String(input);
  }
  return Array.from(input).map((char) => {
    if (accentedCharacterList_default.includes(char)) {
      return encodeURIComponent(char);
    }
    const doSlugify = false;
    return encodeURIComponent(seoUrlPart2(char, doSlugify));
  }).join("");
};
var changeCasing = (word) => {
  const shouldKeepCasing = supportedBooleanOperators_default.includes(word);
  return shouldKeepCasing ? word : word.toLowerCase();
};
function formatForURL_default(input) {
  const data = input || "";
  return data.split(" ").map(formatWord).map(changeCasing).join("+");
}

// src/utils/lrp/urlParts/queryPart.ts
function queryPart_default(query) {
  return query ? `/${urlPrefixes_default.query}/${formatForURL_default(query)}` : "";
}

// src/utils/lrp/urlParts/categoriesPart.ts
function categoriesPart_default({ l1Category, l2Category, l2Categories } = {}) {
  const isL1CategoryValid = l1Category?.key;
  const isL2CategoryValid = l2Category?.key;
  const validL2Categories = (l2Categories || []).filter((cat) => cat.key);
  const isL2CategoriesValid = validL2Categories.length > 0;
  const isValid = isL1CategoryValid || isL2CategoryValid || isL2CategoriesValid;
  let part = isValid ? `/${urlPrefixes_default.baseCategoriesURL}` : "";
  part += isL1CategoryValid ? `/${l1Category?.key}` : "";
  part += isL2CategoryValid && !isL2CategoriesValid ? `/${l2Category?.key}` : "";
  part += isL2CategoriesValid ? `/${validL2Categories.map((cat) => cat.key).join("+")}` : "";
  return part;
}

// src/utils/lrp/createURLComponent.ts
import { seoUrlPart as seoUrlPart3 } from "@ecg-internal/aurora-js-formatters";
var createURLComponent_default = (urlPart) => seoUrlPart3(urlPart).toLowerCase();

// src/utils/lrp/urlParts/sellerPart.ts
var createSellerNameComponent = (sellerName) => {
  const normalized = createURLComponent_default(sellerName);
  return normalized || "verkoper";
};
var getSellerUrl = (seller) => `/${urlPrefixes_default.seller}/${createSellerNameComponent(seller.name)}/${seller.id}`;
var sellerPart_default = (seller) => seller && Number(seller.id) > 0 && seller.name ? getSellerUrl(seller) : "";

// src/utils/lrp/urlParts/pageIndexPart.ts
var pageIndexPart_default = (pageIndex) => Number(pageIndex) > 1 ? `/${urlPrefixes_default.page}/${pageIndex}` : "";

// src/utils/lrp/urlParts/attributesPart.ts
var attributesPart_default = (attributes) => {
  if (Array.isArray(attributes) && attributes.length) {
    const labels = attributes.map((attr) => createURLComponent_default(attr.attributeValueLabel)).join(urlPrefixes_default.seoFriendlyMergeKey);
    const ids = attributes.map((attr) => attr.attributeValueId).join(urlPrefixes_default.seoFriendlyMergeKey);
    return `/${urlPrefixes_default.attributes}/${labels}/${ids}`;
  }
  return "";
};

// src/constants/viewOptions.ts
var viewOptions_default = {
  GALLERY_VIEW: "gallery-view",
  LIST_VIEW: "list-view"
};

// src/constants/nonStandarts.ts
var defaults = {
  attributes: ["Altijd"],
  limit: 30,
  view: viewOptions_default.LIST_VIEW
};

// src/constants/standarts.ts
var maxSeoAttributes = 2;

// src/utils/lrp/getSortedAttributes.ts
var getSortedAttributes_default = (attributes, attributesOrder = []) => {
  const seoFriendlyAttributes = [];
  const hashFriendlyAttributes = {};
  const pushValueKey = (attributeKey, attributeValue) => {
    if (hashFriendlyAttributes[attributeKey]) {
      hashFriendlyAttributes[attributeKey].push(encodeURIComponent(attributeValue.attributeValueKey));
    } else {
      hashFriendlyAttributes[attributeKey] = [encodeURIComponent(attributeValue.attributeValueKey)];
    }
  };
  const pushValueId = (attributeValue) => {
    const attributeKey = urlPrefixes_default.attributes;
    if (hashFriendlyAttributes[attributeKey]) {
      hashFriendlyAttributes[attributeKey].push(attributeValue.attributeValueId);
    } else {
      hashFriendlyAttributes[attributeKey] = [attributeValue.attributeValueId];
    }
  };
  const seoFriendlyKeys = Object.keys(attributes).map((key) => attributesOrder.indexOf(key) > -1 && key);
  Object.keys(attributes).sort((a, b) => attributesOrder.indexOf(a) - attributesOrder.indexOf(b)).filter((key) => attributes[key].length).forEach((key) => {
    if (seoFriendlyKeys.indexOf(key) > -1 && Object.keys(seoFriendlyAttributes).length < maxSeoAttributes) {
      seoFriendlyAttributes.push(attributes[key][0]);
      attributes[key].slice(1, attributes[key].length).forEach(pushValueId);
    } else if (attributes[key][0].attributeValueId) {
      attributes[key].forEach(pushValueId);
    } else if (defaults.attributes.indexOf(attributes[key][0].attributeValueKey) === -1) {
      attributes[key].forEach((value) => pushValueKey(key, value));
    }
  });
  return {
    seoFriendlyAttributes,
    hashFriendlyAttributes
  };
};

// src/utils/lrp/urlParts/hashPart.ts
var hashPart_default = (hashedFriendlyAttributes) => {
  Object.keys(hashedFriendlyAttributes).forEach(
    (key) => hashedFriendlyAttributes[key] === null && delete hashedFriendlyAttributes[key]
  );
  const keys = Object.keys(hashedFriendlyAttributes);
  return keys.map((key) => {
    const attrValues = hashedFriendlyAttributes[key];
    const values = Array.isArray(attrValues) ? attrValues.join(",") : attrValues;
    return `${key}${urlPrefixes_default.nonTrackingDefiner}${values}`;
  }).join(urlPrefixes_default.nonTrackingSeparator);
};

// src/utils/lrp/formatQuery.ts
import { seoUrlPart as seoUrlPart4 } from "@ecg-internal/aurora-js-formatters";
var formatWord2 = (input) => {
  if (input === null || typeof input === "undefined") {
    return "";
  }
  if (typeof input === "number") {
    return String(input);
  }
  return Array.from(input).map((char) => {
    if (accentedCharacterList_default.includes(char)) {
      return encodeURIComponent(char);
    }
    const doSlugify = false;
    return encodeURIComponent(seoUrlPart4(char, doSlugify));
  }).join("");
};
var changeCasing2 = (word) => {
  const shouldKeepCasing = supportedBooleanOperators_default.includes(word);
  return shouldKeepCasing ? word : word.toLowerCase();
};
var formatQuery_default = (query) => query.split(" ").map(formatWord2).map(changeCasing2).join("+");

// src/utils/lrp/getRangeAttributeKeyValuePair.ts
var getRangeAttributeKeyValuePair = ({ from, to, attributeKey }) => {
  const part = {};
  if (typeof from !== "undefined" && from !== null) {
    part[`${attributeKey}From`] = from;
  }
  if (typeof to !== "undefined" && to !== null) {
    part[`${attributeKey}To`] = to;
  }
  return part;
};
var getRangeAttributeKeyValuePair_default = getRangeAttributeKeyValuePair;

// src/utils/lrp/includeViewOptions.ts
var includeViewOptions = (view, isViewOptionsClicked) => view && (isViewOptionsClicked || view === "gallery-view");
var includeViewOptions_default = includeViewOptions;

// src/utils/lrp/savedSearchesAttributes.ts
var createSavedSearchAttrs = (attributesByKey) => {
  const savedSearchAttrs = { [urlPrefixes_default.asSavedSearch]: "true" };
  const offeredSince = attributesByKey && attributesByKey.find((attr) => attr.attributeKey === "offeredSince");
  if (offeredSince) {
    savedSearchAttrs[urlPrefixes_default.offeredSince] = offeredSince.attributeValueKey;
  }
  return savedSearchAttrs;
};
var savedSearchesAttributes_default = createSavedSearchAttrs;

// src/constants/sortAttributeNames.ts
var attributeNames = {
  SORT_BY: "sortBy",
  SORT_ATTRIBUTE: "sortAttribute",
  SORT_ORDER: "sortOrder"
};
var sortAttributeNames_default = attributeNames;

// src/utils/lrp/urlParts/getURLQueryString.ts
var filterURLSearchParams = (urlQueryParameters) => Object.entries(urlQueryParameters || {}).filter(
  ([, value]) => typeof value === "string" || typeof value === "number"
);
var getURLQueryString = (urlQueryParameters) => {
  const filteredUrlSearchParams = filterURLSearchParams(urlQueryParameters);
  if (filteredUrlSearchParams.length === 0) {
    return "";
  }
  return `?${new URLSearchParams(filteredUrlSearchParams).toString()}`;
};
var getURLQueryString_default = getURLQueryString;

// src/pages/lrpUrl.ts
var lrpUrl_default = ({
  asSavedSearch,
  attributes = {},
  attributesByKey,
  bypassSpellingSuggestion,
  categories,
  distance = 0,
  isViewOptionsClicked,
  pageIndex,
  pageSize = 0,
  postcode,
  query,
  rangeAttributes,
  removeAllAttributes = false,
  removeAllFilters = false,
  searchInTitleAndDescription = false,
  seller,
  seoFriendlyAttributes,
  showQueryInSeoFriendlyPart = true,
  sortOptions,
  traits,
  urlQueryParameters,
  view
}) => {
  const keepAttributes = !removeAllFilters && !removeAllAttributes;
  const sortedAttributes = getSortedAttributes_default(attributes, seoFriendlyAttributes);
  const queryUrl = showQueryInSeoFriendlyPart ? queryPart_default(query) : "";
  const categoriesUrl = !removeAllFilters ? categoriesPart_default(categories) : "";
  const sellerUrl = sellerPart_default(seller);
  const createAttributesUrl = keepAttributes ? attributesPart_default(sortedAttributes.seoFriendlyAttributes) : "";
  const pageIndexUrl = pageIndexPart_default(pageIndex);
  const urlQueryString = getURLQueryString_default(urlQueryParameters);
  const rangeAttributesData = rangeAttributes ? rangeAttributes.map(getRangeAttributeKeyValuePair_default) : [];
  const savedSearch = asSavedSearch;
  const hashFriendlyAttributes = keepAttributes && sortedAttributes.hashFriendlyAttributes;
  const hashFriendlyRangeAttributes = keepAttributes && Object.assign({}, ...rangeAttributesData);
  const hashFriendlySavedSearch = keepAttributes && savedSearch && savedSearchesAttributes_default(attributesByKey);
  const hashedPart = hashPart_default({
    [urlPrefixes_default.query]: !showQueryInSeoFriendlyPart && query ? formatQuery_default(query) : null,
    [sortAttributeNames_default.SORT_BY]: keepAttributes && sortOptions?.sortBy ? sortOptions?.sortBy : null,
    [sortAttributeNames_default.SORT_ORDER]: keepAttributes && sortOptions?.sortOrder ? sortOptions?.sortOrder : null,
    [sortAttributeNames_default.SORT_ATTRIBUTE]: keepAttributes && sortOptions?.sortAttribute ? sortOptions?.sortAttribute : null,
    [urlPrefixes_default.distance]: !removeAllFilters && Number(distance) > 0 ? distance : null,
    [urlPrefixes_default.postcode]: !removeAllFilters && postcode ? postcode.toUpperCase() : null,
    [urlPrefixes_default.searchInTitleAndDescription]: !removeAllFilters && searchInTitleAndDescription ? true : null,
    [urlPrefixes_default.view]: (!removeAllFilters || query) && includeViewOptions_default(view, isViewOptionsClicked) ? view : null,
    [urlPrefixes_default.Limit]: Number(pageSize) > 0 ? pageSize : null,
    [urlPrefixes_default.bypassSpellingSuggestion]: bypassSpellingSuggestion ? true : null,
    [urlPrefixes_default.traits]: !removeAllFilters && traits && traits.length > 0 ? traits : null,
    ...hashFriendlyAttributes,
    ...hashFriendlyRangeAttributes,
    ...hashFriendlySavedSearch
  });
  const hashedUrl = hashedPart ? `${urlPrefixes_default.nonTrackingKey}${hashedPart}` : "";
  return `${sellerUrl}${categoriesUrl}${queryUrl}${createAttributesUrl}${pageIndexUrl}/${urlQueryString}${hashedUrl}`;
};

// src/pages/l1Url.ts
import { seoUrlPart as seoUrlPart5 } from "@ecg-internal/aurora-js-formatters";

// src/constants/carsL1Category.ts
var CARS_L1_CATEGORY_NAME = {
  "mp-nl-NL": "auto-kopen",
  "twh-nl-BE": "auto-kopen",
  "twh-fr-BE": "acheter-voiture"
};
var CARS_L1_CATEGORY_ID = 91;

// src/pages/l1Url.ts
var l1Url = ({ l1CategoryId, l1CategoryName, tenant, locale }) => {
  let catName = l1CategoryName;
  if (Number(l1CategoryId) === CARS_L1_CATEGORY_ID) {
    catName = CARS_L1_CATEGORY_NAME[`${tenant}-${locale}`];
  }
  return `/cp/${l1CategoryId}/${seoUrlPart5(catName)}`;
};
var l1Url_default = l1Url;

// src/pages/l15Url.ts
import { seoUrlPart as seoUrlPart6 } from "@ecg-internal/aurora-js-formatters";
var l15Url = ({ l1CategoryName }) => {
  const seoFriendlyCategoryName = seoUrlPart6(l1CategoryName);
  return {
    withQueryPageUrl: ({ query }) => {
      return `/l/${seoFriendlyCategoryName}/q/${formatForURL_default(query)}/`;
    },
    withCategoryBucketPageUrl: ({ categoryBucket }) => {
      return `/l/${seoFriendlyCategoryName}/${seoUrlPart6(categoryBucket)}/`;
    }
  };
};
var l15Url_default = l15Url;

// src/pages/lrpUrlV2/withSeoFriendlyAttributes.ts
import { seoUrlPart as seoUrlPart7 } from "@ecg-internal/aurora-js-formatters";
var getLrpBaseWithCategories = (l1CategoryId, l2CategoryId) => `/${urlPrefixes_default.baseCategoriesURL}/${seoUrlPart7(l1CategoryId)}/${seoUrlPart7(l2CategoryId)}/`;
var withSeoFriendlyAttributes = (params) => {
  const multiOrSingle = () => {
    const attributeArr = [...params.attributeValues];
    const firstAttribute = attributeArr.shift();
    const valueFormatted = seoUrlPart7(firstAttribute?.key || "");
    const divider = `${urlPrefixes_default.nonTrackingKey}${urlPrefixes_default.attributes}${urlPrefixes_default.nonTrackingDefiner}`;
    const restOfTheAttributesIds = attributeArr?.map((attr) => attr.id).join(",");
    const restValues = attributeArr.length ? `${divider}${restOfTheAttributesIds}` : "";
    return `${getLrpBaseWithCategories(params.l1Category, params.l2Category)}${urlPrefixes_default.attributes}/${valueFormatted}/${firstAttribute?.id}/${restValues}`;
  };
  const formatters = {
    MULTI_SELECT: multiOrSingle,
    SINGLE_SELECT: multiOrSingle,
    NUMERIC: () => {
      const value = params.attributeValue || "";
      const valueFormatted = value.split(" ").join(urlPrefixes_default.seoFriendlyMergeKey);
      return `${getLrpBaseWithCategories(params.l1Category, params.l2Category)}${urlPrefixes_default.query}/${valueFormatted}/`;
    },
    TEXT: () => {
      const { key, label } = params.attributeValues[0] ?? {};
      return `${getLrpBaseWithCategories(params.l1Category, params.l2Category)}${urlPrefixes_default.textAttribute}/${key}/${label}/`;
    }
  };
  return formatters[params.attributeType] ? formatters[params.attributeType]() : "";
};
var withSeoFriendlyAttributes_default = withSeoFriendlyAttributes;

// src/pages/lrpUrlV2/index.ts
var lrpUrlV2 = {
  withSeoFriendlyAttributes: withSeoFriendlyAttributes_default
};
var lrpUrlV2_default = lrpUrlV2;
export {
  asqModalUrl_default as asqModalUrl,
  asqUrl_default as asqUrl,
  l15Url_default as l15Url,
  l1Url_default as l1Url,
  lrpUrl_default as lrpUrl,
  lrpUrlV2_default as lrpUrlV2,
  vipUrl_default as vipUrl
};
//# sourceMappingURL=index.mjs.map